import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { usePublicExperiences } from "@/hooks/usePublicCatalog";
import {
  MAX_REVIEW_PHOTOS,
  submitReview,
  uploadReviewPhoto,
  type ReviewPhoto,
} from "@/lib/reviewSubmissions";
import Reveal from "@/components/base/Reveal";

type Status = "idle" | "loading" | "success" | "error";

const inputClass =
  "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400";
const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

const GUIDELINES = [
  "Tell us which farm or experience you visited.",
  "Be honest and specific — what stood out most?",
  "Mention the people, the food or the season if you like.",
  "We review every submission before it appears publicly.",
];

interface SelectedPhoto {
  file: File;
  preview: string;
}

export default function ReviewsShareForm() {
  const { data: experiences } = usePublicExperiences();
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [photoError, setPhotoError] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const photosRef = useRef<SelectedPhoto[]>([]);

  photosRef.current = photos;

  // Release object URLs if the visitor navigates away mid-selection.
  useEffect(
    () => () => {
      photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview));
    },
    [],
  );

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    setPhotoError("");
    setPhotos((current) => {
      const next = [...current];
      for (const file of files) {
        if (next.length >= MAX_REVIEW_PHOTOS) break;
        if (!file.type.startsWith("image/")) continue;
        if (file.size > 8 * 1024 * 1024) {
          setPhotoError("Each photo must be 8 MB or smaller.");
          continue;
        }
        next.push({ file, preview: URL.createObjectURL(file) });
      }
      return next;
    });
  };

  const removePhoto = (index: number) => {
    setPhotos((current) => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  };

  const clearPhotos = () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview));
    setPhotos([]);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      clearPhotos();
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const comment = String(formData.get("comment") || "").trim();
    const experienceValue = String(formData.get("experience") || "").trim();
    const matched = experiences.find((entry) => entry.summary.slug === experienceValue);

    if (!name || !comment) {
      setStatus("error");
      setError("Please add your name and a few words about your visit before sending.");
      return;
    }

    setStatus("loading");
    setError("");

    try {
      const uploaded: ReviewPhoto[] = [];
      for (let index = 0; index < photos.length; index += 1) {
        const stored = await uploadReviewPhoto(photos[index].file);
        uploaded.push({ src: stored, alt: `${name} — visit photo ${index + 1}` });
      }

      await submitReview({
        visitor_name: name,
        role: String(formData.get("role") || "").trim(),
        location: String(formData.get("location") || "").trim(),
        village: String(formData.get("village") || "").trim(),
        experience: matched ? matched.summary.title : experienceValue,
        experience_slug: matched ? matched.summary.slug : "",
        rating,
        comment,
        visit_date: String(formData.get("visit_date") || "").trim(),
        photos: uploaded,
      });
      clearPhotos();
      setStatus("success");
      setRating(5);
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't send your review just now. Please try again in a moment.",
      );
    }
  };

  if (status === "success") {
    return (
      <section id="share" className="bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8">
          <Reveal className="bg-background-50 border border-background-200 rounded-lg p-8 md:p-12 text-center max-w-3xl mx-auto">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-star-smile-line text-3xl"></i>
            </span>
            <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mt-6">
              Thank you for sharing your experience.
            </h3>
            <p className="text-foreground-600 leading-relaxed mt-3 max-w-md mx-auto">
              Your review is now with our team for a quick check. Once approved it will appear here
              alongside the other visitor stories.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="mt-7 inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> Share another review
            </button>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section id="share" className="sun-glaze bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-9">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-900">
                <i className="ri-chat-quote-line text-2xl"></i>
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mt-5">
                Share Your Experience
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                Visited a farm or joined an experience? Tell other travellers what it was like.
              </p>

              <form id="share-review-form" onSubmit={handleSubmit} className="mt-7 space-y-5">
                <div>
                  <span className={labelClass}>
                    Your rating <span className="text-accent-600">*</span>
                  </span>
                  <div className="flex items-center gap-1.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHover(star)}
                        onMouseLeave={() => setHover(0)}
                        aria-label={`${star} star${star > 1 ? "s" : ""}`}
                        className="w-9 h-9 flex items-center justify-center cursor-pointer text-2xl leading-none"
                      >
                        <i
                          className={
                            star <= (hover || rating) ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"
                          }
                        ></i>
                      </button>
                    ))}
                    <span className="ml-1 text-sm text-foreground-600">{rating}.0 / 5</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rv-name" className={labelClass}>
                      Your name <span className="text-accent-600">*</span>
                    </label>
                    <input id="rv-name" name="name" type="text" required placeholder="e.g. Anil Kumar" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="rv-experience" className={labelClass}>
                      Which experience?
                    </label>
                    <select id="rv-experience" name="experience" defaultValue="" className={`${inputClass} cursor-pointer`}>
                      <option value="">Choose an experience…</option>
                      {experiences.map((entry) => (
                        <option key={entry.summary.slug} value={entry.summary.slug}>
                          {entry.summary.title}
                        </option>
                      ))}
                      <option value="Another farm visit">Another farm visit</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rv-role" className={labelClass}>
                      You are a
                    </label>
                    <input id="rv-role" name="role" type="text" placeholder="e.g. Traveller, Teacher, Parent" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="rv-location" className={labelClass}>
                      City / place
                    </label>
                    <input id="rv-location" name="location" type="text" placeholder="e.g. Hyderabad" className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rv-village" className={labelClass}>
                      Farm village (if known)
                    </label>
                    <input id="rv-village" name="village" type="text" placeholder="e.g. Amalapuram" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="rv-date" className={labelClass}>
                      Date of visit
                    </label>
                    <input id="rv-date" name="visit_date" type="date" className={`${inputClass} cursor-pointer`} />
                  </div>
                </div>

                <div>
                  <label htmlFor="rv-comment" className={labelClass}>
                    Your review <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="rv-comment"
                    name="comment"
                    rows={5}
                    maxLength={500}
                    required
                    placeholder="What did you see, taste and feel on the farm?"
                    className={`${inputClass} resize-none`}
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1.5">Up to 500 characters.</p>
                </div>

                <div>
                  <span className={labelClass}>
                    Add visit photos <span className="text-foreground-400 font-normal">(optional)</span>
                  </span>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {photos.map((photo, index) => (
                      <div
                        key={photo.preview}
                        className="relative aspect-square rounded-md overflow-hidden bg-background-100 border border-background-200"
                      >
                        <img
                          src={photo.preview}
                          alt={`Selected visit photo ${index + 1}`}
                          className="w-full h-full object-cover object-top"
                          decoding="async"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          aria-label="Remove photo"
                          className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_REVIEW_PHOTOS ? (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="aspect-square rounded-md border border-dashed border-background-300 bg-background-100 flex flex-col items-center justify-center text-foreground-500 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-camera-line text-2xl"></i>
                        <span className="text-[11px] mt-1">Add photo</span>
                      </button>
                    ) : null}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="sr-only"
                    onChange={addFiles}
                  />
                  <p className="text-xs text-foreground-500 mt-1.5">
                    Up to {MAX_REVIEW_PHOTOS} photos, 8 MB each. Your photos appear on the visitor wall once approved.
                  </p>
                  {photoError ? (
                    <p className="flex items-center gap-2 text-xs text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-3 py-2 mt-2">
                      <i className="ri-error-warning-line"></i>
                      <span>{photoError}</span>
                    </p>
                  ) : null}
                </div>

                <div className="form-extra-contact" aria-hidden="true">
                  <label htmlFor="rv-website-alt">Leave this field empty</label>
                  <input
                    id="rv-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && error ? (
                  <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-4 py-3">
                    <i className="ri-error-warning-line text-lg"></i>
                    <span>{error}</span>
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>{" "}
                      {photos.length ? "Uploading photos…" : "Sending…"}
                    </>
                  ) : (
                    <>
                      Submit my review <i className="ri-send-plane-fill"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 md:p-8">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-900">
                <i className="ri-lightbulb-line text-2xl"></i>
              </span>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-semibold mt-5">
                What makes a great review
              </h3>
              <ul className="mt-5 space-y-3.5">
                {GUIDELINES.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-700 shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-6 border-t border-secondary-200">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Every review helps a farmer reach more people and keeps these traditions alive.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}